import { useEffect } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import { Map } from 'immutable';

import { cxHelpers } from 'util/className';
import { EVENT_ACTIONS, TRACK_ACTION_TYPES } from 'util/tracking_constants';
import { useTrackUx } from 'util/uxEvents';

const { cxEl } = cxHelpers('EmployeeInformationWidget');

interface EmployeeInformationFormProps {
  user: Map<string, any>;
  editMode: boolean;
  errors: Record<string, any>;
  setFieldValue: (field: string, value: any) => void;
  values: Record<string, any>;
  handleShowRemoteTeamMemberAlert: (values: Record<string, any>) => void;
  renderLegalName: (
    editMode: boolean,
    errors: Record<string, any>
  ) => React.ReactNode;
  renderDateOfBirth: (
    editMode: boolean,
    errors: Record<string, any>
  ) => React.ReactNode;
  renderBusinessName: (editMode: boolean) => React.ReactNode;
  renderEin: (
    editMode: boolean,
    errors: Record<string, any>
  ) => React.ReactNode;
  renderSSN: (
    editMode: boolean,
    errors: Record<string, any>,
    setFieldValue: (field: string, value: any) => void,
    values: Record<string, any>
  ) => React.ReactNode;
  renderAddress: (
    editMode: boolean,
    errors: Record<string, any>
  ) => React.ReactNode;
  renderIsRemoteCheckBox: (
    editMode: boolean,
    values: Record<string, any>
  ) => React.ReactNode;
  showIsRemoteField: boolean;
  disableEdits: boolean;
  showEmployeeWillBeNotifiedAlert?: boolean;
  showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert?: boolean;
}

const EmployeeInformationForm = ({
  user,
  editMode,
  errors,
  setFieldValue,
  values,
  handleShowRemoteTeamMemberAlert,
  renderLegalName,
  renderDateOfBirth,
  renderBusinessName,
  renderEin,
  renderSSN,
  renderAddress,
  renderIsRemoteCheckBox,
  showIsRemoteField,
  disableEdits,
  showEmployeeWillBeNotifiedAlert,
  showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert,
}: EmployeeInformationFormProps) => {
  const trackUx = useTrackUx();
  // Track when temp users encounter disabled fields in edit mode
  useEffect(() => {
    const trackingKey = `tracked_disabled_fields_${user.get('id')}`;
    const hasTracked = sessionStorage.getItem(trackingKey);

    if (editMode && disableEdits && !hasTracked) {
      trackUx(EVENT_ACTIONS.ALERT_VIEWED, TRACK_ACTION_TYPES.VIEW, {
        alert_type: 'disabled_fields_temp_user',
        reason: 'temp_user_pending_invitation',
        form_section: 'employee_information',
      });
      sessionStorage.setItem(trackingKey, 'true');
    }
    const CLEAR_INTERVAL = 3000;
    const intervalId = setInterval(() => {
      sessionStorage.removeItem(trackingKey);
    }, CLEAR_INTERVAL);

    return () => clearInterval(intervalId);
  }, [editMode, disableEdits, trackUx, user]);

  return (
    <Box column className={cxEl(editMode ? 'edit-mode' : 'view-mode')}>
      {handleShowRemoteTeamMemberAlert(values)}
      {editMode && showEmployeeWillBeNotifiedAlert && (
        <Box bgcolor="blue100" p={12} mb={24} bradiusl>
          <Text
            i18n="team.employee_profile.payroll_ee_information.banner_content"
            i18nProps={{ employee_first_name: user.get('first_name') }}
          />
        </Box>
      )}
      {editMode && showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert && (
        <Box bgcolor="blue100" p={12} mb={24} bradiusl>
          <Text
            i18n="product_usage.hover_text.edit_button_disabled_text"
            i18nProps={{ employee_name: user?.get('first_name') }}
          />
        </Box>
      )}
      {renderLegalName(editMode, errors)}
      {renderDateOfBirth(editMode, errors)}
      {user.get('business_contractor') && renderBusinessName(editMode)}
      {user.get('business_contractor')
        ? renderEin(editMode, errors)
        : renderSSN(editMode, errors, setFieldValue, values)}
      {renderAddress(editMode, errors)}
      {showIsRemoteField && renderIsRemoteCheckBox(editMode, values)}
    </Box>
  );
};

export default EmployeeInformationForm;
